import { useState } from "react";
import axios from "axios";
import { Modal, ModalBody, ModalHeader, ModalTitle } from "react-bootstrap";
import Table from "../../../components/Table";
import { ENDPOINT } from "../../../constants/endpointConstants";
import { getToken, getUserData } from "../../../utils/utils";
import { PAYMENT_METHOD } from "../../../constants/sharedConstans";
import DeliveryReceiptForm from "../../../components/DeliveryReceiptForm";

const CashierOpenSales = ({ sales, itemClick, paySaleParent, isLoading }) => {
  const [paySaleModal, setPaySaleModal] = useState(false);
  const [saleId, setSaleId] = useState("");
  const [totalPrice, setTotalPrice] = useState(0);
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [hasDiscount, setHasDiscount] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);

  const [payments, setPayments] = useState([{ paymentMethodId: 0, amount: 0 }]);

  const [cancelSaleModal, setCancelSaleModal] = useState(false);
  const [cancelSaleId, setCancelSaleId] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelErrorMsg, setCancelErrorMsg] = useState("");

  const paySale = (sale) => {
    setTotalPrice(
      sale.saleProducts.reduce(
        (total, product) => total + product.productPrice * product.amount,
        0
      )
    );
    setDeliveryPrice(sale.deliveryPrice);
    setSaleId(sale.saleId);
    setPaySaleModal(true);
  };

  const closePaySale = () => {
    setErrorMsg("");
    setPayments([{ paymentMethodId: 0, amount: 0 }]);
    setDiscount(0);
    setHasDiscount(false);
    setPaySaleModal(false);
  };

  const cancelSaleClick = (sale) => {
    setCancelSaleId(sale.saleId);
    setCancelErrorMsg("");
    setCancelSaleModal(true);
  };

  const closeCancelSale = () => {
    setCancelSaleModal(false);
    setCancelSaleId(null);
    setCancelErrorMsg("");
  };

  const confirmCancelSale = async () => {
    const token = getToken();
    try {
      setCancelLoading(true);
      await axios.post(
        ENDPOINT.POST_CANCEL_SALE,
        { saleId: cancelSaleId },
        token
      );
      setCancelLoading(false);
      paySaleParent(cancelSaleId);
      closeCancelSale();
    } catch (error) {
      setCancelLoading(false);
      setCancelErrorMsg(
        error.response?.data?.code || "Error al cancelar el pedido"
      );
    }
  };

  const cancelButtonContent = () => {
    if (cancelLoading) {
      return (
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
      );
    }
    return "SI";
  };

  const discountChange = (event) => {
    const value = event.target.value;
    if (value === "") {
      setDiscount(0);
    } else {
      const discountValue = parseInt(value);
      setDiscount(discountValue);
    }
  };

  const addPaymentRow = () => {
    const availableMethods = PAYMENT_METHOD.filter(
      (method) => !payments.some((p) => p.paymentMethodId === method.id)
    );
    if (availableMethods.length > 0) {
      setPayments([
        ...payments,
        { paymentMethodId: availableMethods[0].id, amount: 0 },
      ]);
    }
  };

  const removePaymentRow = (index) => {
    if (payments.length > 1) {
      setPayments(payments.filter((_, i) => i !== index));
    }
  };

  const updatePayment = (index, field, value) => {
    const newPayments = [...payments];
    if (field === "paymentMethodId") {
      newPayments[index].paymentMethodId = parseInt(value);
    } else {
      newPayments[index].amount = value === "" ? 0 : parseInt(value);
    }
    setPayments(newPayments);
  };

  const getTotalPaid = () => {
    return payments.reduce((total, payment) => total + payment.amount, 0);
  };

  const getRemainingAmount = () => {
    const total = calculateTotalPrice(totalPrice, discount);
    return total - getTotalPaid();
  };

  const calculateTotalPrice = (totalPrice, discount) => {
    return totalPrice - (totalPrice * discount) / 100 + deliveryPrice;
  };

  const payFinish = async () => {
    const total = calculateTotalPrice(totalPrice, discount);

    const CASH_PAYMENT_METHOD_ID = 0;
    const nonCashTotal = payments
      .filter((p) => p.paymentMethodId !== CASH_PAYMENT_METHOD_ID)
      .reduce((sum, p) => sum + p.amount, 0);

    const sale = {
      saleId,
      discount,
      payments: payments.map((p) => {
        let amount = p.amount;
        if (p.paymentMethodId === CASH_PAYMENT_METHOD_ID) {
          amount = Math.max(0, total - nonCashTotal);
        }
        return {
          paymentMethodId: p.paymentMethodId,
          amount: amount,
        };
      }),
      totalPrice: total,
    };
    const token = getToken();
    try {
      setLoading(true);
      await axios.post(ENDPOINT.POST_PAY_SALE, sale, token);

      paySaleParent(saleId);
      setHasDiscount(false);
      setDiscount(0);
      setPaySaleModal(false);
      setLoading(false);
      setPayments([{ paymentMethodId: 0, amount: 0 }]);
    } catch (error) {
      setLoading(false);
      setErrorMsg(error.response?.data?.code || "Error al procesar el pago");
    }
  };

  const isButtonDisabled = () => {
    const total = calculateTotalPrice(totalPrice, discount);
    const totalPaid = getTotalPaid();
    const CASH_PAYMENT_METHOD_ID = 0;

    const nonCashPayments = payments.filter(
      (p) => p.paymentMethodId !== CASH_PAYMENT_METHOD_ID
    );

    if (loading) return true;
    if (errorMsg) return true;
    if (discount > 50) return true;
    if (totalPaid < total) return true;
    if (payments.some((p) => p.amount <= 0)) return true;
    if (
      payments.length === 1 &&
      nonCashPayments.length === 1 &&
      nonCashPayments[0].amount !== total
    )
      return true;
    if (payments.length > 1 && nonCashPayments.some((p) => p.amount >= total))
      return true;

    return false;
  };

  const buttonLoading = () => {
    if (loading === false) {
      return "Pagar";
    } else {
      return (
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
      );
    }
  };

  const isAdmin = getUserData()?.role === 1;

  const openSalesColumn = [
    {
      name: "#",
      key: "dailyOrderNumber",
      colSize: "col-1",
      type: "text",
    },
    {
      name: "Nombre",
      key: "clientName",
      colSize: "col-3",
      type: "text",
    },
    {
      name: "Tiempo",
      key: "createdAt",
      colSize: "col-2",
      type: "timer",
    },
    {
      name: "Valor",
      key: "totalPriceToPay",
      colSize: "col-3",
      type: "currency",
    },
    {
      name: "",
      type: "actions",
      colSize: "col-3",
      buttons: [
        {
          iconClass: "fa-solid fa-hand-holding-dollar",
          action: paySale,
        },
        {
          iconClass: "fa-solid fa-truck",
          action: (sale) => handleDeliveryClick(sale),
        },
        ...(isAdmin
          ? [
              {
                iconClass: "fa-solid fa-trash-can",
                action: cancelSaleClick,
              },
            ]
          : []),
      ],
    },
  ];

  const handleDeliveryClick = (sale) => {
    setSelectedSale(sale);
    setShowDeliveryModal(true);
  };

  const hasDiscountChange = (event) => {
    const value = event.target.checked;
    setHasDiscount(value);
    setDiscount(0);
  };

  return (
    <>
      <div>
        <div className="row text-center fw-bold">
          <h2>Pedidos Abiertos</h2>
        </div>
        <div className="row ps-md-5 ps-0 mt-4">
          <Table
            columnNames={openSalesColumn}
            items={sales}
            fixSize="t-responsive-medium"
            itemClick={itemClick}
            isLoading={isLoading}
          />
        </div>
        <Modal show={paySaleModal} onHide={closePaySale}>
          <ModalBody>
            <div className="row justify-content-center text-center mb-4">
              <div className="d-flex align-items-center">
                <p className="m-0 w-50 fw-bold text-end">Comida:</p>
                <p className="m-0 ms-2 w-50 text-start">
                  {"$" +
                    new Intl.NumberFormat("es-CL").format(
                      totalPrice - (totalPrice * discount) / 100
                    )}
                </p>
              </div>
              <div className="d-flex align-items-center">
                <p className="m-0 w-50 fw-bold text-end">Domicilio:</p>
                <p className="m-0 ms-2 w-50 text-start">
                  {"$" + new Intl.NumberFormat("es-CL").format(deliveryPrice)}
                </p>
              </div>
              <div className="row">
                <p className="mb-0 mt-4 fs-4">
                  <span className="fw-bold">Total: </span>
                  {"$" +
                    new Intl.NumberFormat("es-CL").format(
                      calculateTotalPrice(totalPrice, discount)
                    )}
                </p>
              </div>
            </div>
            <div className="row justify-content-center mb-3">
              <div className="col-md-9">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Métodos de Pago</h5>
                </div>

                {payments.map((payment, index) => (
                  <div key={index} className="row mb-2 align-items-center">
                    <div className="col-md-5">
                      <select
                        className="form-select"
                        value={payment.paymentMethodId}
                        onChange={(e) =>
                          updatePayment(
                            index,
                            "paymentMethodId",
                            e.target.value
                          )
                        }
                      >
                        {PAYMENT_METHOD.filter(
                          (method) =>
                            !payments.some(
                              (p, i) =>
                                i !== index && p.paymentMethodId === method.id
                            )
                        ).map((method, i) => (
                          <option value={method.id} key={i}>
                            {method.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-5">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Monto"
                        value={payment.amount === 0 ? "" : payment.amount}
                        onChange={(e) =>
                          updatePayment(index, "amount", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-md-2">
                      {payments.length > 1 && (
                        <button
                          className="btn btn-sm btn-dark"
                          onClick={() => removePaymentRow(index)}
                          title="Eliminar método de pago"
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {payments.length < PAYMENT_METHOD.length && (
                  <button
                    className="btn btn-sm btn-dark"
                    onClick={addPaymentRow}
                    title="Agregar método de pago"
                  >
                    <i className="fa-solid fa-plus"></i>
                  </button>
                )}

                <div className="mt-3 p-2 bg-light rounded">
                  <div className="d-flex justify-content-between">
                    <span className="fw-bold">Total pagado:</span>
                    <span>
                      ${new Intl.NumberFormat("es-CL").format(getTotalPaid())}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="fw-bold">Cambio:</span>
                    <span
                      className={
                        getRemainingAmount() <= 0
                          ? "text-success"
                          : "text-danger"
                      }
                    >
                      $
                      {new Intl.NumberFormat("es-CL").format(
                        getRemainingAmount()
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="row mt-4 justify-content-center align-items-center">
              <div className="col-md-4 fw-bold d-flex">
                <p className="mb-0 me-2"> Descuento (%) </p>
                <input
                  className="form-check-input"
                  type="checkbox"
                  value={hasDiscount}
                  onChange={hasDiscountChange}
                ></input>
              </div>
              <div className="col-md-5">
                {hasDiscount && (
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Descuento %"
                    value={discount === 0 ? "" : discount}
                    onChange={discountChange}
                  />
                )}
              </div>
            </div>
            <div className="row mt-4">
              <div className="col-md-12 text-center">
                <button
                  className="btn btn-primary size mb-0"
                  onClick={payFinish}
                  disabled={isButtonDisabled()}
                >
                  {buttonLoading()}
                </button>
              </div>
            </div>
            {errorMsg && (
              <div className="row mt-4">
                <p className="error-msg">{errorMsg}</p>
              </div>
            )}
          </ModalBody>
        </Modal>
        <Modal
          show={showDeliveryModal}
          onHide={() => {
            setShowDeliveryModal(false);
            setSelectedSale(null);
          }}
        >
          <ModalHeader closeButton className="style-modal-header">
            <ModalTitle className="text-modal">Detalles de Entrega</ModalTitle>
          </ModalHeader>
          <ModalBody>
            {selectedSale && (
              <DeliveryReceiptForm
                userName={selectedSale.clientName || "Cliente"}
                total={selectedSale.totalPrice || 0}
                onClose={() => {
                  setShowDeliveryModal(false);
                  setSelectedSale(null);
                }}
              />
            )}
          </ModalBody>
        </Modal>
        <Modal show={cancelSaleModal} onHide={closeCancelSale}>
          <ModalHeader closeButton className="style-modal-header">
            <ModalTitle className="text-modal"> CANCELAR PEDIDO </ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p className="text-center">
              {" "}
              Estas seguro que deseas cancelar este pedido?{" "}
            </p>
            <div className="row">
              <div className="col-6">
                <button
                  type="button"
                  className="btn btn-primary btn-sm w-100 text-modal mt-3"
                  onClick={confirmCancelSale}
                  disabled={cancelLoading}
                >
                  {" "}
                  {cancelButtonContent()}{" "}
                </button>
              </div>
              <div className="col-6">
                <button
                  type="button"
                  className="btn btn-primary btn-sm w-100 text-modal mt-3"
                  onClick={closeCancelSale}
                >
                  {" "}
                  NO{" "}
                </button>
              </div>
            </div>
            {cancelErrorMsg && (
              <div className="row mt-4">
                <p className="error-msg">{cancelErrorMsg}</p>
              </div>
            )}
          </ModalBody>
        </Modal>
      </div>
    </>
  );
};

export default CashierOpenSales;
